import React, { useEffect, useRef, useState } from 'react';
import { Camera, Trash2, Check, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

export const ACCENTS = [
  ['blue', 'Speed Blue', '217 88% 55%'],
  ['graphite', 'Graphite', '215 12% 62%'],
  ['emerald', 'Emerald', '158 64% 42%'],
  ['amber', 'Amber', '38 92% 52%'],
  ['crimson', 'Crimson', '354 78% 52%'],
  ['violet', 'Violet', '265 75% 60%'],
];
const LANDINGS = [['/app', 'Visão Geral'], ['/app/crm', 'CRM / Leads'], ['/app/deals', 'Deals / F&I'],
  ['/app/estoque', 'Estoque'], ['/app/agenda', 'Agenda'], ['/app/reports', 'Relatórios'], ['/app/detail', 'Detail / Clean']];

export const applyAccent = (key) => {
  const a = ACCENTS.find(([k]) => k === key);
  if (a) document.documentElement.style.setProperty('--primary', a[2]);
  else document.documentElement.style.removeProperty('--primary');
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { (async () => { try { const { data } = await api.get('/users/me'); setP(data); } catch (e) {} })(); }, []);

  if (!p) return <p className="ent-empty">Carregando perfil…</p>;
  const prefs = p.prefs || {};

  const patch = async (body, msg) => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/me', body);
      setP(data);
      if (setUser) setUser((u) => ({ ...u, name: data.name, picture: data.picture, prefs: data.prefs }));
      if (msg) toast({ title: msg });
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const setPref = (k, v) => {
    if (k === 'accent') applyAccent(v);
    setP({ ...p, prefs: { ...prefs, [k]: v } });
    patch({ prefs: { [k]: v } });
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/users/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setP({ ...p, picture: data.picture });
      if (setUser) setUser((u) => ({ ...u, picture: data.picture }));
      toast({ title: 'Foto de perfil atualizada' });
    } catch (e) { toast({ title: 'Erro ao enviar a foto', description: e.response?.data?.detail || '' }); }
    finally { setUploading(false); }
  };

  const removePhoto = async () => {
    try {
      await api.delete('/users/me/avatar');
      setP({ ...p, picture: '' });
      if (setUser) setUser((u) => ({ ...u, picture: '' }));
    } catch (e) {}
  };

  const initials = (p.name || 'U').split(' ').filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();

  return (
    <div className="max-w-4xl space-y-6" data-testid="profile-page">
      <div>
        <p className="ent-label">Minha conta</p>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight">Meu perfil</h1>
      </div>

      <div className="flex flex-wrap items-center gap-6 border-y border-border py-5">
        <div className="relative">
          {p.picture ? (
            <img src={p.picture} alt={p.name} data-testid="profile-avatar" className="h-20 w-20 object-cover" style={{ borderRadius: 3, border: '1px solid hsl(var(--border))' }} />
          ) : (
            <div data-testid="profile-avatar" className="flex h-20 w-20 items-center justify-center border border-border bg-muted/30 font-display text-2xl font-black text-muted-foreground" style={{ borderRadius: 3 }}>{initials}</div>
          )}
          <button data-testid="profile-avatar-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center border border-border bg-card transition-colors hover:border-primary" style={{ borderRadius: 3 }}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <Camera className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
          <input ref={fileRef} data-testid="profile-file" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-xl font-bold tracking-tight">{p.name}</p>
          <p className="text-xs text-muted-foreground">{p.job_title || p.role} · login {p.login || '—'}</p>
          <div className="mt-2 flex gap-2">
            <button onClick={() => fileRef.current?.click()} className="ent-btn ent-btn-sm">Trocar foto</button>
            {p.picture && <button data-testid="profile-remove-photo" onClick={removePhoto} className="ent-btn ent-btn-sm"><Trash2 className="h-3 w-3" /> Remover</button>}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground/70">JPG, PNG ou WebP até 8MB. A foto aparece no topo do sistema e nas conversas.</p>
        </div>
      </div>

      <div>
        <p className="cm-section">Dados pessoais</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[['name', 'Nome de exibição'], ['job_title', 'Cargo / título'], ['phone', 'Telefone'], ['email', 'E-mail']].map(([k, label]) => (
            <div key={k}>
              <p className="ent-label mb-1">{label}</p>
              <input data-testid={`profile-${k}`} className="ent-input w-full" defaultValue={p[k] || ''}
                onBlur={(e) => e.target.value !== (p[k] || '') && patch({ [k]: e.target.value }, 'Perfil atualizado')} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="cm-section">Personalização</p>
        <div className="space-y-4">
          <div>
            <p className="ent-label mb-1.5">Cor de destaque do sistema</p>
            <div className="flex flex-wrap gap-2">
              {ACCENTS.map(([k, label, hsl]) => (
                <button key={k} data-testid={`profile-accent-${k}`} onClick={() => setPref('accent', k)}
                  className={`flex items-center gap-2 border px-3 py-1.5 text-[11.5px] font-semibold transition-colors ${(prefs.accent || 'blue') === k ? 'border-primary text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  style={{ borderRadius: 3 }}>
                  <span className="h-3 w-3" style={{ background: `hsl(${hsl})`, borderRadius: 2 }} />
                  {label}
                  {(prefs.accent || 'blue') === k && <Check className="h-3 w-3 text-primary" />}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="ent-label mb-1">Tela inicial ao entrar</p>
              <select data-testid="profile-landing" className="ent-input w-full" value={prefs.landing || '/app'} onChange={(e) => setPref('landing', e.target.value)}>
                {LANDINGS.map(([v, l]) => <option key={v} value={v} className="bg-popover text-foreground">{l}</option>)}
              </select>
            </div>
            <div>
              <p className="ent-label mb-1">Como quer ser chamado</p>
              <input data-testid="profile-nickname" className="ent-input w-full" placeholder="Ex: Felipe" defaultValue={prefs.nickname || ''}
                onBlur={(e) => e.target.value !== (prefs.nickname || '') && setPref('nickname', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {saving && <p className="text-[11px] text-muted-foreground">salvando…</p>}
    </div>
  );
};

export default Profile;
